import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CountryList } from '@/components/country-list';
import { getTableCellErrorState } from '@/components/ui-v2/inputs/table/hooks/get-table-cell-error-state';
import { Command, CommandInput } from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useIsCellEditing } from '@/hooks/use-is-cell-editing';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import {
  DealItem,
  DealItemWithVersions,
} from '@/lib/graphql/generated/graphql';
import { useCountryNames } from '@/lib/hooks/useCountryNames';
import { cn } from '@/lib/utils';
import { createSubRowSortingFn } from '@/lib/utils/table-filter-utils';
import { useSetColumnSort } from '@/states/column-sort.state';
import { setEditingCellAtom } from '@/states/editing-cell.state';
import { Row } from '@tanstack/react-table';
import { useSetAtom } from 'jotai';
import { ChevronsUpDown } from 'lucide-react';

import { ExtendedColumnDef } from '../table-products';
import { SortIcon } from './sort';
import { TableCellWithRecentlyModified } from './table-cell-with-recently-modified';

interface ColumnProps {
  handleCellClick: (id: string, field: keyof DealItem) => void;
  handleCellEdit: <K extends keyof DealItem>(
    id: string | undefined,
    field: K,
    value: DealItem[K],
    isLeafRow?: boolean
  ) => Promise<void>;
}

interface MadeInCellContentProps {
  row: Row<DealItemWithVersions>;
  handleCellClick: ColumnProps['handleCellClick'];
  handleCellEdit: ColumnProps['handleCellEdit'];
  countries: Record<string, string>;
}

function MadeInCellContent({
  row,
  handleCellClick,
  handleCellEdit,
  countries,
}: MadeInCellContentProps) {
  const { t } = useTranslation();
  const setEditingCell = useSetAtom(setEditingCellAtom);
  const isOfferLocked = useIsOfferLocked();
  const isEditing = useIsCellEditing(row, 'madeIn');
  const isParent = row.subRows.length > 1;

  const errorState = getTableCellErrorState({
    row,
    field: 'madeIn',
    errorType: 'none',
  });

  const getValue = () => {
    const value = row.original.activeVersion.madeIn;
    if (typeof value === 'string')
      return countries[value as keyof typeof countries];
    return '';
  };

  const getParentValue = () => {
    const allSame = row.subRows.every(
      (subRow) =>
        subRow.original.activeVersion.madeIn ===
        row.original.activeVersion.madeIn
    );
    const value = row.original.activeVersion.madeIn;
    if (!allSame) return '≠';
    if (typeof value === 'string')
      return countries[value as keyof typeof countries];
    return '';
  };

  const currentLabel = isParent ? getParentValue() : getValue();

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setEditingCell(null);
    }
  };

  return (
    <TableCellWithRecentlyModified
      productId={row.original.id}
      field="madeIn"
      isParent={isParent}
    >
      {(isRecentlyModified) => (
        <div
          className={cn(
            'h-full w-full',
            (isParent || isOfferLocked) && 'opacity-50',
            isParent || !isOfferLocked
              ? 'cursor-pointer'
              : 'cursor-not-allowed',
            errorState.hasError && !isEditing
              ? 'border-l-2 border-red-500 bg-red-50'
              : isRecentlyModified && !isEditing
                ? 'border-l-2 border-blue-500 bg-blue-50'
                : ''
          )}
        >
          <Popover open={isEditing} onOpenChange={handleOpenChange}>
            <PopoverTrigger
              asChild
              disabled={isOfferLocked}
              onClick={
                !isParent
                  ? () =>
                      !isOfferLocked &&
                      handleCellClick(row.original.activeVersion.id, 'madeIn')
                  : undefined
              }
            >
              <div
                className={cn(
                  'flex h-full w-full items-center justify-between space-x-2 truncate px-2 text-sm',
                  !isParent && !isOfferLocked && 'cursor-pointer',
                  isEditing && errorState.hasError
                    ? 'border-red-700 ring-1 ring-inset ring-red-700'
                    : isEditing
                      ? 'border-black ring-1 ring-inset ring-black text-foreground'
                      : ''
                )}
              >
                {errorState.isEmpty ? (
                  <span className="text-muted-foreground">
                    {errorState.errorMessage ? t(errorState.errorMessage) : '—'}
                  </span>
                ) : (
                  <span className="truncate">{currentLabel || '—'}</span>
                )}
                {!isParent && (
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                )}
              </div>
            </PopoverTrigger>

            <PopoverContent className="w-[200px] p-0">
              <Command>
                <CommandInput
                  placeholder={t('kpi.made_in_research_placeholder')}
                />
                <CountryList
                  selectedCode={row.original.activeVersion.madeIn}
                  countryNames={countries}
                  onSelect={(code) => {
                    handleCellEdit(
                      row.original.activeVersion.id,
                      'madeIn',
                      code,
                      !isParent
                    );
                    setEditingCell(null);
                  }}
                />
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      )}
    </TableCellWithRecentlyModified>
  );
}

const useColumnDefMadeIn = ({
  handleCellClick,
  handleCellEdit,
}: ColumnProps): ExtendedColumnDef<DealItemWithVersions> => {
  const { t } = useTranslation();
  const { setColumnSort } = useSetColumnSort();
  const countries = useCountryNames();

  const accessorKey: keyof DealItem = 'madeIn';
  return useMemo(
    () => ({
      accessorKey: accessorKey,
      name: 'MadeIn',
      sortingFn: createSubRowSortingFn('madeIn', (row) => {
        const code = row.activeVersion?.madeIn;
        if (typeof code === 'string') {
          return countries[code as keyof typeof countries] ?? null;
        }
        return null;
      }),
      header: () => (
        <div
          className="flex cursor-pointer items-center"
          onClick={() => setColumnSort(accessorKey)}
        >
          {t('kpi.made_in')} <SortIcon pointer="madeIn" />
        </div>
      ),
      cell: ({ row }) => (
        <MadeInCellContent
          row={row}
          handleCellClick={handleCellClick}
          handleCellEdit={handleCellEdit}
          countries={countries}
        />
      ),
      size: 160,
    }),
    [t, setColumnSort, countries, handleCellClick, handleCellEdit]
  );
};

export default useColumnDefMadeIn;
